import React, { useState, lazy, Suspense, useRef, useEffect, useCallback } from 'react';
import { confirmAlert, Button, TwIconButton, Confirmation, Drawer } from 'components/common';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaArrowLeft, FaSyncAlt, FaBolt, FaPlus, FaEdit,
  FaList, FaEllipsisV,
} from 'react-icons/fa';
import { FaBriefcase } from 'react-icons/fa6';
import { MdDelete, MdTrackChanges, MdSync } from 'react-icons/md';
import { FaFaceSadTear, FaRotateLeft } from 'react-icons/fa6';
import { deleteProject, convertQuoteNumber, getProject, rollbackLostProject } from 'redux/crm/projectSlice';
import { escapeHtml } from 'helpers/utils';
import ProjectForm from 'components/app/crm/project/ProjectForm';
import ChangeSalesUnitForm     from './ChangeSalesUnitForm';
import ChangeStatusProjectForm from './ChangeStatusProjectForm';
import SyncToOplaCrmForm       from './SyncToOplaCrmForm';
import CloseLostForm            from './CloseLostForm';

const LazyEnterJobForm    = lazy(() => import('./step/negotiation/EnterJobForm'));
const LazyJobBuildingForm = lazy(() => import('./step/contract/JobBuildingForm'));

// ── Status / pipeline badge ───────────────────────────────────────────────────
const COLOR_CLASSES = {
  primary:   { dot: 'tw-bg-blue-500',    text: 'tw-text-blue-700',    bg: 'tw-bg-blue-50'    },
  success:   { dot: 'tw-bg-emerald-500', text: 'tw-text-emerald-700', bg: 'tw-bg-emerald-50' },
  danger:    { dot: 'tw-bg-red-500',     text: 'tw-text-red-700',     bg: 'tw-bg-red-50'     },
  warning:   { dot: 'tw-bg-amber-500',   text: 'tw-text-amber-700',   bg: 'tw-bg-amber-50'   },
  info:      { dot: 'tw-bg-sky-500',     text: 'tw-text-sky-700',     bg: 'tw-bg-sky-50'     },
  secondary: { dot: 'tw-bg-slate-400',   text: 'tw-text-slate-600',   bg: 'tw-bg-slate-100'  },
};

// Shows the sales pipeline stage (preferred) or falls back to deal status
const StatusBadge = ({ pipeline, status }) => {
  // Sales pipeline badge — uses color from Crm::SalesPipeline
  if (pipeline?.name) {
    const cls = COLOR_CLASSES[pipeline.color] ?? COLOR_CLASSES.secondary;
    return (
      <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-xl tw-text-xs tw-font-semibold ${cls.bg} ${cls.text}`}>
        <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-shrink-0 ${cls.dot}`} />
        {pipeline.name}
      </span>
    );
  }

  // Fallback: deal status badge
  if (status?.name) {
    const cls = COLOR_CLASSES[status.css_class] ?? COLOR_CLASSES.secondary;
    return (
      <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-xl tw-text-xs tw-font-semibold ${cls.bg} ${cls.text}`}>
        <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-shrink-0 ${cls.dot}`} />
        {status.name}
      </span>
    );
  }

  return null;
};
StatusBadge.propTypes = { pipeline: PropTypes.object, status: PropTypes.object };

// ── Icon-only button — thin wrapper over the canonical TwIconButton ──────────
const IconBtn = ({ onClick, title, children, danger = false }) => (
  <TwIconButton
    icon={() => children}
    variant={danger ? 'danger' : 'secondary'}
    size="lg"
    title={title}
    onClick={onClick}
  />
);
IconBtn.propTypes = {
  onClick:  PropTypes.func,
  title:    PropTypes.string,
  children: PropTypes.node.isRequired,
  danger:   PropTypes.bool,
};

// ── Labelled action button ────────────────────────────────────────────────────
const ActionBtn = ({ onClick, title, children, variant = 'default' }) => {
  const cls = variant === 'info'
    ? 'tw-text-sky-600 hover:tw-bg-sky-50 tw-border-sky-200'
    : variant === 'danger'
      ? 'tw-text-red-600 hover:tw-bg-red-50 tw-border-red-200'
      : variant === 'warning'
        ? 'tw-text-amber-600 hover:tw-bg-amber-50 tw-border-amber-200'
        : '';
  return (
    <Button
      variant={variant === 'primary' ? 'primary' : 'secondary'}
      size="sm"
      title={title}
      onClick={onClick}
      className={cls}
    >
      {children}
    </Button>
  );
};
ActionBtn.propTypes = {
  onClick:  PropTypes.func,
  title:    PropTypes.string,
  children: PropTypes.node.isRequired,
  variant:  PropTypes.oneOf(['default', 'primary', 'info', 'danger', 'warning']),
};

// ── Dropdown ──────────────────────────────────────────────────────────────────
const DropdownItem = ({ icon: Icon, iconCls, label, onClick, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-flex tw-items-center tw-gap-2.5 tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-left tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-rounded tw-mx-1 tw-my-0.5 ${
      danger
        ? 'tw-text-red-600 hover:tw-bg-red-50'
        : 'tw-text-slate-700 hover:tw-bg-slate-50'
    }`}
    style={{ width: 'calc(100% - 8px)' }}
  >
    <span className={`tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md ${danger ? 'tw-bg-red-50' : 'tw-bg-slate-100'}`}>
      <Icon size={12} className={iconCls || (danger ? 'tw-text-red-500' : 'tw-text-slate-500')} />
    </span>
    {label}
  </button>
);
DropdownItem.propTypes = {
  icon:    PropTypes.elementType.isRequired,
  iconCls: PropTypes.string,
  label:   PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  danger:  PropTypes.bool,
};

const DropdownGroup = ({ label, items, danger = false, isLast }) => (
  <div className="tw-px-1">
    {label && (
      <p className="tw-px-3 tw-pt-2 tw-pb-1 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-mb-0">
        {label}
      </p>
    )}
    {items.map((item, i) => (
      <DropdownItem key={i} {...item} danger={danger || item.danger} />
    ))}
    {!isLast && <div className="tw-border-t tw-border-slate-100 tw-my-1" />}
  </div>
);
DropdownGroup.propTypes = {
  label:  PropTypes.string,
  items:  PropTypes.array.isRequired,
  danger: PropTypes.bool,
  isLast: PropTypes.bool,
};

// ── ProjectDetailHeader ───────────────────────────────────────────────────────
const ProjectDetailHeader = ({ onRefresh }) => {
  const [showDeleteConfirm,   setShowDeleteConfirm]   = useState(false);
  const [showChangeSalesUnit, setShowChangeSalesUnit] = useState(false);
  const [showChangeStatus,    setShowChangeStatus]    = useState(false);
  const [showSyncOpla,        setShowSyncOpla]        = useState(false);
  const [showCloseLost,       setShowCloseLost]       = useState(false);
  const [showEnterJobForm,    setShowEnterJobForm]    = useState(false);
  const [showJobBuildingForm, setShowJobBuildingForm] = useState(false);
  const [showEditDrawer,      setShowEditDrawer]      = useState(false);
  const [dropdownOpen,        setDropdownOpen]        = useState(false);

  const { recordData: project } = useSelector(state => state.crm_project);
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const { id: projectId } = useParams();
  const dropdownRef = useRef(null);

  const {
    allow_edit, allow_delete, allow_create_waiver,
    allow_convert_quotation, allow_change_sales_unit,
    allow_sync_sales_process_opla, allow_close_lost, allow_rollback_lost,
    allow_create_enter_job,
    allow_create_job_building,
  } = project?.permissions || {};

  const pipelineId      = parseInt(project?.sales_pipeline?.id) || 0;
  const hasEnterJobs    = !!project?.has_enter_jobs;
  const hasJobBuildings = !!project?.has_job_buildings;
  // Show Enter Job button at Concept Design (3), Proposal Submission (4) or
  // Proposal Revision (5) — see CRM_SALES_PIPELINE_* in app/lib/constants.rb.
  const canShowEnterJob    = allow_create_enter_job    && [3, 4, 5].includes(pipelineId);
  // Show Job Building button only when enter jobs exist, no job buildings yet, and permission granted
  const canShowJobBuilding = allow_create_job_building && hasEnterJobs && !hasJobBuildings;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  const handleRefresh = useCallback(async () => {
    // Prefer the page-level refresh (re-fetches the project AND remounts the
    // active tab so every visible slice reloads). Fall back to a project-only
    // refresh when rendered without the prop.
    if (onRefresh) {
      onRefresh();
    } else {
      await dispatch(getProject(projectId));
    }
    toast.success('Project refreshed.');
  }, [dispatch, projectId, onRefresh]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const response = await dispatch(deleteProject(project)).unwrap();
      if (!response.error) navigate('/crm/projects/list');
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error(error.message);
    }
  }, [dispatch, navigate, project]);

  // Reopening rewrites a closed deal and pushes the restored stage back to Opla
  // CRM, so it asks first. The confirm spells out what comes back, because the
  // snapshot is consumed on use — there is no second undo.
  const handleReopen = useCallback(() => {
    // 'derived' means the project was closed before the undo snapshot existed, so
    // the previous status is rebuilt from the REV# records. Say so plainly rather
    // than promising an exact restore.
    const derived = project?.lost_rollback_source === 'derived';

    confirmAlert({
      title: 'Reopen project',
      message: `Undo <strong>Close lost</strong> for "<strong>${escapeHtml(project?.name)}</strong>"? ` +
        (derived
          ? 'This project was closed before the undo was available, so the previous status is rebuilt ' +
            'from its REV# records. Check the result and adjust with Change status if needed — a building ' +
            'that had already entered the job phase cannot have its job status recovered.'
          : 'The project and the buildings it closed go back to the status they had before. ' +
            'This can only be undone once.') +
        ' The stage is pushed to Opla CRM again.',
      type: 'warning',
      buttons: [
        {
          label: 'Reopen', variant: 'primary',
          onClick: async () => {
            try {
              await dispatch(rollbackLostProject(project.id)).unwrap();
              toast.success('Close lost was rolled back.');
              onRefresh?.();
            } catch (error) {
              toast.error(error?.message || 'Failed to reopen the project.');
            }
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [dispatch, project, onRefresh]);

  const menuGroups = [
    {
      label: 'Management',
      items: [
        allow_change_sales_unit && {
          icon: MdTrackChanges, iconCls: 'tw-text-sky-500', label: 'Change Sales Unit',
          onClick: () => { setShowChangeSalesUnit(true); closeDropdown(); },
        },
        // allow_change_status && {
        //   icon: MdOutlineChangeCircle, iconCls: 'tw-text-amber-500', label: 'Change Status',
        //   onClick: () => { setShowChangeStatus(true); closeDropdown(); },
        // },
      ].filter(Boolean),
    },
    {
      label: 'Integration',
      items: [
        allow_sync_sales_process_opla && {
          icon: MdSync, iconCls: 'tw-text-primary-500', label: 'Sync to Opla CRM',
          onClick: () => { setShowSyncOpla(true); closeDropdown(); },
        },
      ].filter(Boolean),
    },
    {
      danger: true,
      items: [
        allow_delete && {
          icon: MdDelete, label: 'Delete Project', danger: true,
          onClick: () => { setShowDeleteConfirm(true); closeDropdown(); },
        },
      ].filter(Boolean),
    },
  ].filter(g => g.items.length > 0);

  const projectName  = project?.name;
  const pcvNumber    = project?.pcv_number;
  const status        = project?.status;
  const salesPipeline = project?.sales_pipeline;

  return (
    <>
      {/* One row on a desktop. On a phone the action buttons alone are wider than
          the screen, and because both they and the status badges refuse to
          shrink the bar used to overflow — buttons printed on top of the project
          name. It wraps instead: identity on its own line, actions beneath. */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-px-3 tw-py-2 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm">

        {/* ── Left: back + project identity ── */}
        <IconBtn onClick={() => navigate(-1)} title="Back to list">
          <FaArrowLeft size={12} />
        </IconBtn>

        <div className="tw-hidden sm:tw-block tw-w-px tw-h-5 tw-bg-slate-200 tw-shrink-0" />

        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1">
          {projectName && (
            <span className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-truncate tw-max-w-xs">
              {projectName}
            </span>
          )}
          {pcvNumber && (
            <span className="tw-shrink-0 tw-text-[11px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-slate-100 tw-text-slate-500">
              {pcvNumber}
            </span>
          )}
          <StatusBadge pipeline={salesPipeline} status={status} />
        </div>

        {/* ── Right: actions ── */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-1.5 tw-w-full sm:tw-w-auto sm:tw-shrink-0">

          <IconBtn onClick={handleRefresh} title="Refresh">
            <FaSyncAlt size={12} />
          </IconBtn>

          <IconBtn onClick={() => navigate('/crm/projects/list')} title="Project List">
            <FaList size={12} />
          </IconBtn>

          {allow_convert_quotation && (
            <ActionBtn variant="info" onClick={() => dispatch(convertQuoteNumber(projectId))} title="Convert Quote number">
              <FaBolt size={11} />
              <span className="tw-hidden lg:tw-inline">Convert Quote</span>
            </ActionBtn>
          )}

          {allow_create_waiver && (
            <ActionBtn variant="info"
              onClick={() => navigate(`/crm/projects/${projectId}/waivers/new`, { state: { project } })}
              title="New Waiver"
            >
              <FaPlus size={11} />
              <span className="tw-hidden lg:tw-inline">New Waiver</span>
            </ActionBtn>
          )}

          {canShowEnterJob && (
            <ActionBtn variant="info" onClick={() => setShowEnterJobForm(true)} title="Create Enter Job">
              <FaBriefcase size={11} />
              <span className="tw-hidden lg:tw-inline">Create Enter Job</span>
            </ActionBtn>
          )}

          {canShowJobBuilding && (
            <ActionBtn variant="info" onClick={() => setShowJobBuildingForm(true)} title="Create Job Building">
              <FaPlus size={11} />
              <span className="tw-hidden lg:tw-inline">Job Building</span>
            </ActionBtn>
          )}

          {allow_close_lost && (
            <ActionBtn variant="danger" onClick={() => setShowCloseLost(true)} title="Close Lost">
              <FaFaceSadTear size={11} />
              <span className="tw-hidden lg:tw-inline">Close Lost</span>
            </ActionBtn>
          )}

          {/* Takes the place of Close Lost once the whole deal is lost — the
              backend hides one and offers the other, they never appear together. */}
          {allow_rollback_lost && (
            <ActionBtn variant="warning" onClick={handleReopen} title="Undo Close Lost and restore the previous status">
              <FaRotateLeft size={11} />
              <span className="tw-hidden lg:tw-inline">Reopen Project</span>
            </ActionBtn>
          )}

          {allow_edit && (
            <ActionBtn variant="primary"
              onClick={() => setShowEditDrawer(true)}
              title="Edit Project"
            >
              <FaEdit size={11} />
              <span className="tw-hidden sm:tw-inline">Edit Project</span>
            </ActionBtn>
          )}

          {menuGroups.length > 0 && (
            <div ref={dropdownRef} className="tw-relative">
              <IconBtn onClick={() => setDropdownOpen(o => !o)} title="More actions">
                <FaEllipsisV size={11} />
              </IconBtn>

              {dropdownOpen && (
                <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1.5 tw-w-56 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-xl tw-z-50 tw-py-1">
                  {menuGroups.map((group, gi) => (
                    <DropdownGroup
                      key={gi}
                      label={group.label}
                      items={group.items}
                      danger={group.danger}
                      isLast={gi === menuGroups.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <Confirmation
        show={showDeleteConfirm}
        type="danger"
        title="Delete Project"
        message={<span>Are you sure you want to delete project <strong className="tw-text-red-600">{pcvNumber}</strong>?</span>}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {showChangeSalesUnit && (
        <ChangeSalesUnitForm showModal={showChangeSalesUnit} setshowModal={setShowChangeSalesUnit} project={project} />
      )}
      {showChangeStatus && (
        <ChangeStatusProjectForm showModal={showChangeStatus} setshowModal={setShowChangeStatus} project={project} />
      )}
      {showSyncOpla && (
        <SyncToOplaCrmForm showModal={showSyncOpla} setshowModal={setShowSyncOpla} project={project} />
      )}
      {showCloseLost && (
        <CloseLostForm showModal={showCloseLost} setshowModal={setShowCloseLost} project={project} />
      )}
      {showEnterJobForm && (
        <Suspense fallback={null}>
          <LazyEnterJobForm showModal setshowModal={setShowEnterJobForm} project={project} enterJob={null} title="Create Enter Job"
            onSuccess={() => dispatch(getProject(projectId))} />
        </Suspense>
      )}
      {showJobBuildingForm && (
        <Suspense fallback={null}>
          <LazyJobBuildingForm showModal setshowModal={setShowJobBuildingForm} project={project} jobBuilding={null} title="Create Job Building"
            onSuccess={() => dispatch(getProject(projectId))} />
        </Suspense>
      )}

      <Drawer
        show={showEditDrawer}
        onHide={() => setShowEditDrawer(false)}
        title={<><FaEdit size={14} className="tw-text-blue-500" /> Edit Project</>}
        width="70%"
        minWidth="640px"
        backdropClick={false}
      >
        {showEditDrawer && (
          <ProjectForm
            title="Edit Project"
            project={project}
            onClose={() => setShowEditDrawer(false)}
            onSuccess={() => {
              setShowEditDrawer(false);
              dispatch(getProject(projectId));
            }}
          />
        )}
      </Drawer>
    </>
  );
};

ProjectDetailHeader.propTypes = {
  onRefresh: PropTypes.func,
};

export default ProjectDetailHeader;
